import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';

export function ScopeBadge(props: { scope: 'global' | 'doc' }): React.JSX.Element {
  return (
    <span className={`pdf-studio-scope pdf-studio-scope--${props.scope}`}>
      {props.scope === 'global' ? 'Global' : 'This doc'}
    </span>
  );
}

export function AccordionSection(props: {
  id: string;
  title: string;
  variant: 'info' | 'header' | 'title' | 'body' | 'footer' | 'overrides' | 'custom';
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(props.defaultOpen ?? true);
  return (
    <section
      id={props.id}
      className={`pdf-studio-accordion pdf-studio-accordion--${props.variant}${open ? ' is-open' : ''}`}
    >
      <button
        type="button"
        className="pdf-studio-accordion-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{props.title}</span>
        <span className="pdf-studio-accordion-chevron" aria-hidden>
          ▼
        </span>
      </button>
      {open ? <div className="pdf-studio-accordion-body">{props.children}</div> : null}
    </section>
  );
}

const POPOVER_WIDTH = 220;
const POPOVER_HEIGHT = 240;

export function ColorField(props: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  scope?: 'global' | 'doc';
  hasOverride?: boolean;
  onReset?: () => void;
}): React.JSX.Element {
  const { label, value, onChange, disabled, scope, hasOverride, onReset } = props;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  const updatePopoverPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < POPOVER_HEIGHT + 12;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - POPOVER_WIDTH - 8
    );
    setPopoverStyle({
      position: 'fixed',
      left,
      top: showAbove ? Math.max(8, rect.top - POPOVER_HEIGHT - 8) : rect.bottom + 8,
      zIndex: 10000
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePopoverPosition();
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      const portal = document.getElementById('pdf-studio-color-popover-root');
      if (portal?.contains(target)) return;
      setOpen(false);
    };
    const onReposition = () => updatePopoverPosition();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePopoverPosition]);

  const popover =
    open && !disabled
      ? createPortal(
          <div
            id="pdf-studio-color-popover-root"
            className="pdf-studio-swatch-popover pdf-studio-swatch-popover--portal"
            style={popoverStyle}
          >
            <HexColorPicker color={value} onChange={onChange} />
          </div>,
          document.body
        )
      : null;

  return (
    <div className="pdf-studio-field">
      <div className="pdf-studio-field-label-row">
        <span>{label}</span>
        <span className="pdf-studio-field-actions">
          {scope ? <ScopeBadge scope={scope} /> : null}
          {scope === 'doc' && hasOverride && onReset ? (
            <button type="button" className="pdf-studio-reset-btn" onClick={onReset} title="Reset to global">
              Reset
            </button>
          ) : null}
        </span>
      </div>
      <div className="pdf-studio-color-row pdf-studio-swatch-pop" ref={wrapRef}>
        <button
          type="button"
          className="pdf-studio-swatch"
          disabled={disabled}
          style={{ background: value }}
          aria-label={`${label} color`}
          aria-expanded={open}
          onClick={() => {
            if (disabled) return;
            setOpen((v) => {
              const next = !v;
              if (next) requestAnimationFrame(updatePopoverPosition);
              return next;
            });
          }}
        />
        <input
          className="pdf-studio-hex-input"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        {popover}
      </div>
    </div>
  );
}

export function SliderField(props: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
  scope?: 'global' | 'doc';
  hasOverride?: boolean;
  onReset?: () => void;
  onChange: (n: number) => void;
}): React.JSX.Element {
  const { label, value, min, max, step = 1, unit = '', disabled, scope, hasOverride, onReset, onChange } = props;
  return (
    <div className="pdf-studio-field">
      <div className="pdf-studio-field-label-row">
        <span>{label}</span>
        <span className="pdf-studio-field-actions">
          {scope ? <ScopeBadge scope={scope} /> : null}
          {scope === 'doc' && hasOverride && onReset ? (
            <button type="button" className="pdf-studio-reset-btn" onClick={onReset} title="Reset to global">
              Reset
            </button>
          ) : null}
        </span>
      </div>
      <div className="pdf-studio-slider-row">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <span className="pdf-studio-slider-value">
          {value}
          {unit}
        </span>
      </div>
    </div>
  );
}

export function TextField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  scope?: 'global' | 'doc';
  type?: 'text' | 'email' | 'url' | 'tel';
}): React.JSX.Element {
  const { label, value, onChange, placeholder, disabled, scope, type = 'text' } = props;
  return (
    <label className="pdf-studio-field">
      <div className="pdf-studio-field-label-row">
        <span>{label}</span>
        {scope ? <ScopeBadge scope={scope} /> : null}
      </div>
      <input
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function TextAreaField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  scope?: 'global' | 'doc';
}): React.JSX.Element {
  const { label, value, onChange, placeholder, rows = 3, disabled, scope } = props;
  return (
    <label className="pdf-studio-field">
      <div className="pdf-studio-field-label-row">
        <span>{label}</span>
        {scope ? <ScopeBadge scope={scope} /> : null}
      </div>
      <textarea
        rows={rows}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function SelectField<T extends string>(props: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
  disabled?: boolean;
  scope?: 'global' | 'doc';
  hasOverride?: boolean;
  onReset?: () => void;
}): React.JSX.Element {
  const { label, value, options, onChange, disabled, scope, hasOverride, onReset } = props;
  return (
    <label className="pdf-studio-field">
      <div className="pdf-studio-field-label-row">
        <span>{label}</span>
        <span className="pdf-studio-field-actions">
          {scope ? <ScopeBadge scope={scope} /> : null}
          {scope === 'doc' && hasOverride && onReset ? (
            <button type="button" className="pdf-studio-reset-btn" onClick={onReset} title="Reset to global">
              Reset
            </button>
          ) : null}
        </span>
      </div>
      <select disabled={disabled} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ToggleField(props: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  scope?: 'global' | 'doc';
  hasOverride?: boolean;
  onReset?: () => void;
  hint?: string;
}): React.JSX.Element {
  const { label, checked, onChange, disabled, scope, hasOverride, onReset, hint } = props;
  return (
    <div className="pdf-studio-field">
      <label className="pdf-studio-row">
        <input
          type="checkbox"
          disabled={disabled}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="pdf-studio-row-label">{label}</span>
        <span className="pdf-studio-field-actions">
          {scope ? <ScopeBadge scope={scope} /> : null}
          {scope === 'doc' && hasOverride && onReset ? (
            <button type="button" className="pdf-studio-reset-btn" onClick={onReset} title="Reset to global">
              Reset
            </button>
          ) : null}
        </span>
      </label>
      {hint ? <p className="pdf-studio-hint">{hint}</p> : null}
    </div>
  );
}
